import { makeActionCreator } from '../../config/store/utils'
import { onShowSnackbar } from '../SnackbarProvider/SnackbarProvider.actions'
import {
  getAverageBehaviorIndicator,
  getAverageDimensions,
  getDimensions,
  getDimensionsIndicators,
  getPeriodsMonths,
  getPeriodsYears,
  generateBehaviorIndicatorsPdf,
  getMeasurementsIndicators
} from '../../services/behavior'
import {
  transformDataToChart,
  transformMeasurementsIndicators,
  transformMonthsOptions,
  transformYearsOptions,
  transformDimensionsOptions,
  transformDimensionsIndicators,
  calculateDefaultYearAndMonth
} from './BehaviorSection.utils'
import { BEHAVIOR_INDICATORS_ERROR } from './BehaviorSection.constants'
import { safeServiceCall } from '../../modules/utils/helpers'

export const GET_AVERAGE_BEHAVIOR_DATA = 'GET_AVERAGE_BEHAVIOR_DATA'
export const GET_AVERAGE_BEHAVIOR_DATA_SUCCESS = 'GET_AVERAGE_BEHAVIOR_DATA_SUCCESS'
export const GET_AVERAGE_BEHAVIOR_DATA_ERROR = 'GET_AVERAGE_BEHAVIOR_DATA_ERROR'

export const onGetAverageBehaviorData = makeActionCreator(GET_AVERAGE_BEHAVIOR_DATA)
export const onGetAverageBehaviorDataSuccess = makeActionCreator(GET_AVERAGE_BEHAVIOR_DATA_SUCCESS, 'payload')
export const onGetAverageBehaviorDataError = makeActionCreator(GET_AVERAGE_BEHAVIOR_DATA_ERROR, 'payload')

export const onGetAverageBehaviorDataThunk = payload => async dispatch => {
  dispatch(onGetAverageBehaviorData())
  const { dni } = payload
  const { year: fallbackYear, month: fallbackMonth } = calculateDefaultYearAndMonth()

  try {
    const { data } = await getAverageBehaviorIndicator({ dni })
    dispatch(
      onGetAverageBehaviorDataSuccess({
        average: data?.payload?.average ?? 0,
        year: data?.payload?.year ?? fallbackYear,
        month: data?.payload?.month ?? fallbackMonth
      })
    )
  } catch (error) {
    dispatch(
      onGetAverageBehaviorDataError({
        error: error?.originalError?.message ?? BEHAVIOR_INDICATORS_ERROR,
        year: fallbackYear,
        month: fallbackMonth
      })
    )
  }
}

export const GET_BEHAVIOR_DATA = 'GET_BEHAVIOR_DATA'
export const GET_BEHAVIOR_DATA_SUCCESS = 'GET_BEHAVIOR_DATA_SUCCESS'
export const GET_BEHAVIOR_DATA_ERROR = 'GET_BEHAVIOR_DATA_ERROR'

export const onGetBehaviorData = makeActionCreator(GET_BEHAVIOR_DATA)
export const onGetBehaviorDataSuccess = makeActionCreator(GET_BEHAVIOR_DATA_SUCCESS, 'payload')
export const onGetBehaviorDataError = makeActionCreator(GET_BEHAVIOR_DATA_ERROR, 'payload')

export const onGetBehaviorDataThunk = payload => async (dispatch, getState) => {
  dispatch(onGetBehaviorData())

  const { year: defaultYear, month: defaultMonth } = calculateDefaultYearAndMonth()
  const { averageBehaviorYear, averageBehaviorMonth } = getState().behaviorSection

  try {
    const { dni, year, month, fromDownload = false } = payload

    const yearValue = year || averageBehaviorYear || defaultYear
    const monthValue = month || averageBehaviorMonth || defaultMonth

    const [initialDimensionsResult, initialIndicatorResult] = await Promise.all([
      safeServiceCall(() => getAverageDimensions({ dni, year: yearValue, month: monthValue })),
      safeServiceCall(() => getAverageBehaviorIndicator({ dni, year: yearValue, month: monthValue }))
    ])

    let finalDimensionsData = initialDimensionsResult.data
    let finalIndicatorData = initialIndicatorResult.data
    let dimensionsError = initialDimensionsResult.error
    let indicatorError = initialIndicatorResult.error

    const hasIndicatorPayload = !!finalIndicatorData?.payload
    const shouldRetryFromDownload = fromDownload && !hasIndicatorPayload

    if (shouldRetryFromDownload) {
      const retryIndicatorResult = await safeServiceCall(
        () => getAverageBehaviorIndicator({ dni }),
        BEHAVIOR_INDICATORS_ERROR
      )
      if (!retryIndicatorResult.error && retryIndicatorResult.data?.payload) {
        finalIndicatorData = retryIndicatorResult.data
        indicatorError = null

        const retryDimensionsResult = await safeServiceCall(() =>
          getAverageDimensions({
            dni,
            year: retryIndicatorResult.data.payload.year,
            month: retryIndicatorResult.data.payload.month
          })
        )

        if (!retryDimensionsResult.error && retryDimensionsResult.data?.payload) {
          finalDimensionsData = retryDimensionsResult.data
          dimensionsError = null
        }
      }
    }

    if (dimensionsError || indicatorError) {
      dispatch(onGetBehaviorDataError({ error: dimensionsError || indicatorError }))
      return
    }

    const { payload: averageDimensionsPayload } = finalDimensionsData || {}

    const data = {
      dni,
      dimensions: transformDataToChart(averageDimensionsPayload || [], true),
      averageScore: finalIndicatorData?.payload?.average ?? 0
    }

    dispatch(onGetBehaviorDataSuccess(data))
  } catch (error) {
    dispatch(onGetBehaviorDataError({ error: BEHAVIOR_INDICATORS_ERROR }))
  }
}

export const GET_BEHAVIOR_MEASUREMENTS_INDICATORS = 'GET_BEHAVIOR_MEASUREMENTS_INDICATORS'
export const GET_BEHAVIOR_MEASUREMENTS_INDICATORS_SUCCESS = 'GET_BEHAVIOR_MEASUREMENTS_INDICATORS_SUCCESS'
export const GET_BEHAVIOR_MEASUREMENTS_INDICATORS_ERROR = 'GET_BEHAVIOR_MEASUREMENTS_INDICATORS_ERROR'

export const onGetBehaviorMeasurementsIndicators = makeActionCreator(GET_BEHAVIOR_MEASUREMENTS_INDICATORS)
export const onGetBehaviorMeasurementsIndicatorsSuccess = makeActionCreator(
  GET_BEHAVIOR_MEASUREMENTS_INDICATORS_SUCCESS,
  'payload'
)
export const onGetBehaviorMeasurementsIndicatorsError = makeActionCreator(
  GET_BEHAVIOR_MEASUREMENTS_INDICATORS_ERROR,
  'payload'
)

export const onGetBehaviorMeasurementsIndicatorsThunk = payload => async dispatch => {
  dispatch(onGetBehaviorMeasurementsIndicators())

  try {
    const { dni, year, month, dimensionId } = payload
    const { data, errores: error } = await getMeasurementsIndicators({ dni, year, month, dimensionId })
    if (error) {
      dispatch(onGetBehaviorMeasurementsIndicatorsError({ error }))
      return
    }

    const indicators = data?.payload?.measurements?.reduce((acc, curr) => {
      acc[curr.code] = curr.value ?? 0
      return acc
    }, {})

    const transformed = transformMeasurementsIndicators(data?.payload?.measurements || [])

    dispatch(
      onGetBehaviorMeasurementsIndicatorsSuccess({
        indicators,
        data: transformed,
        measurementsUpdatedAt: data?.payload?.lastedUploadDate
      })
    )
  } catch (error) {
    console.error(error)
    dispatch(
      onGetBehaviorMeasurementsIndicatorsError({
        error: BEHAVIOR_INDICATORS_ERROR
      })
    )
  }
}

export const GET_DIMENSIONS = 'GET_DIMENSIONS'
export const GET_DIMENSIONS_SUCCESS = 'GET_DIMENSIONS_SUCCESS'
export const GET_DIMENSIONS_ERROR = 'GET_DIMENSIONS_ERROR'

export const onGetDimensions = makeActionCreator(GET_DIMENSIONS)
export const onGetDimensionsSuccess = makeActionCreator(GET_DIMENSIONS_SUCCESS, 'payload')
export const onGetDimensionsError = makeActionCreator(GET_DIMENSIONS_ERROR, 'payload')

export const onGetDimensionsThunk = () => async dispatch => {
  dispatch(onGetDimensions())

  try {
    const { data } = await getDimensions()
    const transformedDimensions = transformDimensionsOptions(data?.payload || [])
    if (transformedDimensions.length > 0) {
      dispatch(onGetDimensionsSuccess(transformedDimensions))
    } else {
      dispatch(onGetDimensionsSuccess([]))
    }
  } catch (error) {
    dispatch(onGetDimensionsError({ error: 'No se pudo obtener las dimensiones' }))
  }
}

export const GET_DIMENSIONS_INDICATORS = 'GET_DIMENSIONS_INDICATORS'
export const GET_DIMENSIONS_INDICATORS_SUCCESS = 'GET_DIMENSIONS_INDICATORS_SUCCESS'
export const GET_DIMENSIONS_INDICATORS_ERROR = 'GET_DIMENSIONS_INDICATORS_ERROR'

export const onGetDimensionsIndicators = makeActionCreator(GET_DIMENSIONS_INDICATORS)
export const onGetDimensionsIndicatorsSuccess = makeActionCreator(GET_DIMENSIONS_INDICATORS_SUCCESS, 'payload')
export const onGetDimensionsIndicatorsError = makeActionCreator(GET_DIMENSIONS_INDICATORS_ERROR, 'payload')

export const onGetDimensionsIndicatorsThunk = payload => async dispatch => {
  dispatch(onGetDimensionsIndicators())

  try {
    const { dimensionId } = payload
    const { data } = await getDimensionsIndicators({ dimensionId })
    const transformedDimensionsIndicators = transformDimensionsIndicators(data?.payload || [])
    dispatch(onGetDimensionsIndicatorsSuccess(transformedDimensionsIndicators))
  } catch (error) {
    dispatch(onGetDimensionsIndicatorsError({ error: 'No se pudo obtener los indicadores de las dimensiones' }))
  }
}

export const GET_DIMENSIONS_PERIODS_MONTHS = 'GET_DIMENSIONS_PERIODS_MONTHS'
export const GET_DIMENSIONS_PERIODS_MONTHS_SUCCESS = 'GET_DIMENSIONS_PERIODS_MONTHS_SUCCESS'
export const GET_DIMENSIONS_PERIODS_MONTHS_ERROR = 'GET_DIMENSIONS_PERIODS_MONTHS_ERROR'

export const onGetDimensionsPeriodsMonths = makeActionCreator(GET_DIMENSIONS_PERIODS_MONTHS, 'payload')
export const onGetDimensionsPeriodsMonthsSuccess = makeActionCreator(GET_DIMENSIONS_PERIODS_MONTHS_SUCCESS, 'payload')
export const onGetDimensionsPeriodsMonthsError = makeActionCreator(GET_DIMENSIONS_PERIODS_MONTHS_ERROR, 'payload')

export const onGetDimensionsPeriodsMonthsThunk = payload => async (dispatch, getState) => {
  const { year } = payload
  const cached = getState().behaviorSection.monthsByYear?.[year]
  if (cached) {
    return
  }

  dispatch(onGetDimensionsPeriodsMonths({ year }))

  try {
    const { data } = await getPeriodsMonths({ year })
    const transformedMonths = transformMonthsOptions(data?.payload || [])
    dispatch(onGetDimensionsPeriodsMonthsSuccess({ year, months: transformedMonths }))
  } catch (error) {
    dispatch(
      onGetDimensionsPeriodsMonthsError({ error: 'No se pudo obtener los meses de los periodos de las dimensiones' })
    )
  }
}

export const GET_PERIODS_YEARS = 'GET_PERIODS_YEARS'
export const GET_PERIODS_YEARS_SUCCESS = 'GET_PERIODS_YEARS_SUCCESS'
export const GET_PERIODS_YEARS_ERROR = 'GET_PERIODS_YEARS_ERROR'

export const onGetPeriodsYears = makeActionCreator(GET_PERIODS_YEARS)
export const onGetPeriodsYearsSuccess = makeActionCreator(GET_PERIODS_YEARS_SUCCESS, 'payload')
export const onGetPeriodsYearsError = makeActionCreator(GET_PERIODS_YEARS_ERROR, 'payload')

export const onGetPeriodsYearsThunk = () => async dispatch => {
  dispatch(onGetPeriodsYears())
  try {
    const { data } = await getPeriodsYears()
    const transformedYears = transformYearsOptions(data?.payload || [])
    dispatch(onGetPeriodsYearsSuccess(transformedYears))
  } catch (error) {
    dispatch(onGetPeriodsYearsError({ error: 'No se pudo obtener los años de los periodos' }))
  }
}

export const GENERATE_BEHAVIOR_INDICATORS_PDF = 'GENERATE_BEHAVIOR_INDICATORS_PDF'
export const GENERATE_BEHAVIOR_INDICATORS_PDF_SUCCESS = 'GENERATE_BEHAVIOR_INDICATORS_PDF_SUCCESS'
export const GENERATE_BEHAVIOR_INDICATORS_PDF_ERROR = 'GENERATE_BEHAVIOR_INDICATORS_PDF_ERROR'

export const onGenerateBehaviorIndicatorsPdf = makeActionCreator(GENERATE_BEHAVIOR_INDICATORS_PDF)
export const onGenerateBehaviorIndicatorsPdfSuccess = makeActionCreator(
  GENERATE_BEHAVIOR_INDICATORS_PDF_SUCCESS,
  'payload'
)
export const onGenerateBehaviorIndicatorsPdfError = makeActionCreator(GENERATE_BEHAVIOR_INDICATORS_PDF_ERROR, 'payload')

export const onGenerateBehaviorIndicatorsPdfThunk = payload => async dispatch => {
  dispatch(onGenerateBehaviorIndicatorsPdf())

  try {
    const { dni } = payload
    const { data } = await generateBehaviorIndicatorsPdf({ dni })
    dispatch(onGenerateBehaviorIndicatorsPdfSuccess(data))
  } catch (error) {
    console.error(error)
    const errorMEssage = 'No se pudo generar el PDF de los indicadores de Gestión'
    dispatch(onShowSnackbar({ showSnackbar: true, severity: 'error', message: errorMEssage }))
    dispatch(onGenerateBehaviorIndicatorsPdfError({ error: errorMEssage }))
  }
}
